import _ from 'lodash';
import { getCapsuleFormula } from '@/datetime/dateTime.utilities';
import { EMPTY_XY_REGION } from '@/scatterPlot/scatterPlot.constants';
import { toNumber } from '@/utilities/numberHelper.utilities';
import type { ScatterPlotSignal, XYPlotRegion } from '@/scatterPlot/scatterPlot.constants';
import type { ToolParameter } from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { RangeExport } from '@/trendData/duration.types';

type FormulaResult = {
  formula: string;
  formulaParameters: { xSignal: string; ySignal: string };
};

export function getSelectedRegionFromScatterConditionFormula(
  formula: string,
  parameters: ToolParameter[],
): XYPlotRegion {
  const parsedXMin = toNumber(/(\$xSignal > (-?[0-9]+([,.]?)[0-9]*))/.exec(formula)?.[2]);
  const parsedXMax = toNumber(/(\$xSignal < (-?[0-9]+([,.]?)[0-9]*))/.exec(formula)?.[2]);
  const parsedYMin = toNumber(/(\$ySignal > (-?[0-9]+([,.]?)[0-9]*))/.exec(formula)?.[2]);
  const parsedYMax = toNumber(/(\$ySignal < (-?[0-9]+([,.]?)[0-9]*))/.exec(formula)?.[2]);
  const yId = parameters.find((parameter) => parameter.name === 'ySignal')?.item?.id;

  const ys: XYPlotRegion['ys'] = {};
  const region: XYPlotRegion = {
    x: { min: parsedXMin ?? null, max: parsedXMax ?? null },
    ys,
  };

  if (yId) {
    region.ys[yId] = { min: parsedYMin ?? null, max: parsedYMax ?? null };
  }

  return region;
}

export function getScatterConditionFormulaParams(
  region: XYPlotRegion,
  xSeries: ScatterPlotSignal | null,
  ySeries: ScatterPlotSignal | null,
  isBounding: boolean,
  displayRange: RangeExport | undefined,
  isCleansing: boolean,
  minDuration: ValueWithUnitsItem,
  mergeDuration: ValueWithUnitsItem,
): FormulaResult {
  if (!xSeries || !ySeries) {
    throw new Error('Expected series to be present on both axes of the scatter plot.');
  }

  if (!region || _.isEqual(region, EMPTY_XY_REGION)) {
    throw new Error('Expected a region to be selected on the scatter plot, but found no selected region.');
  }

  const yRange =
    region.ys[ySeries.id] ??
    // TODO CRAB-27993: Should always have the correct ID here
    _.chain(region.ys).toArray().head().value();
  const xMin = region.x.min;
  const xMax = region.x.max;
  const yMin = yRange?.min;
  const yMax = yRange?.max;

  if (xMin === null || xMax === null || yMin === null || yMax === null || xMax < xMin || yMax < yMin) {
    throw new Error('Expected maximum value to be greater than minimum value.');
  }

  const formulaParameters = { xSignal: xSeries.id, ySignal: ySeries.id };
  let formula = `($xSignal > ${xMin} \nand $xSignal < ${xMax})\nand ($ySignal > ${yMin} \nand $ySignal < ${yMax})`;

  if (isBounding) {
    if (!displayRange) {
      throw new Error('Expected a display range when bounding capsules.');
    }
    const capsuleFormula = getCapsuleFormula(displayRange);
    const conditionFormula = `condition(${displayRange.duration.asMilliseconds()} ms, ${capsuleFormula})`;
    formula = `(${formula})\nand ${conditionFormula}`;
  }

  if (isCleansing) {
    formula = `(${formula})`;
    formula += mergeDuration.value > 0 ? `\n.merge(${mergeDuration.value}${mergeDuration.units})` : '';
    formula += minDuration.value > 0 ? `\n.removeShorterThan(${minDuration.value}${minDuration.units})` : '';
  }

  return { formula, formulaParameters };
}
